import { Button } from "@9amhealth/shared";
import styled from "@emotion/styled";
import Translate from "components/Translate/Translate";
import type { FC } from "react";
import { IconMdPerson, IconWhiteCheckmark } from "src/constants/icons";
import translate from "src/lib/translate";
import { globalEvents } from "src/constants/globalEvents";
import { tracker } from "src/state/state";
import { AppQueryPopupsController } from "../AppQueryPopupsBloc";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import { APP_BREAKPOINT } from "src/constants/layout";

const StyledInDialog = styled(InDialog)`
  --dialog-background:
    linear-gradient(180deg, #fffcf3 10.42%, rgba(255, 252, 243, 0) 100%),
    linear-gradient(
      89.59deg,
      rgba(128, 174, 255, 0.6) 0.36%,
      rgba(247, 189, 230, 0.6) 49.61%,
      rgba(255, 189, 112, 0.6) 99.86%,
      rgba(247, 189, 230, 0.6) 148.1%,
      rgba(128, 174, 255, 0.6) 193.33%
    );

  --toolbar-background: var(--color-cream);
`;

const HeaderIcon = styled.div`
  width: 5rem;
  height: 5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 2.5rem;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 2px solid #ffffff;
  border-radius: 0.5rem;
  padding: 1.25rem;
`;

const InfoPoint = styled.div`
  display: grid;
  grid-template-columns: auto 3fr;
  margin-bottom: 1.25rem;
  gap: 1rem;
  color: var(--color-charcoal-80);
`;

const CheckmarkWrapper = styled.div`
  margin-top: 0.125rem;
`;

const ButtonsWrap = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;

  @media screen and (max-width: ${APP_BREAKPOINT}px) {
    width: 100%;
    flex-direction: column;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 8.75rem;

  @media screen and (max-width: ${APP_BREAKPOINT}px) {
    width: 100%;
  }
`;

const FastPassPopup: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();

  const onClose = () => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  };

  const handleSkipVisit = () => {
    tracker.track(`Fast Pass - Skipped Visit`);
    document.dispatchEvent(new CustomEvent(globalEvents.SKIP_VISIT));
  };

  const handleScheduleVisit = () => {
    tracker.track(`Fast Pass - Scheduled Visit`);
    document.dispatchEvent(new CustomEvent(globalEvents.SCHEDULE_VISIT));
  };

  return (
    <StyledInDialog
      popup
      title={translate("onboarding.fastPass.title.short")}
      onClose={onClose}
      returnUrl={returnUrl}
    >
      <Container>
        <HeaderWrapper>
          <HeaderIcon>
            <IconMdPerson />
          </HeaderIcon>
          <h4>
            <Translate msg="onboarding.fastPass.title" />
          </h4>
          <p>
            <Translate msg="onboarding.fastPass.description" />
          </p>
        </HeaderWrapper>

        <InfoWrapper>
          <InfoPoint>
            <CheckmarkWrapper>
              <IconWhiteCheckmark />
            </CheckmarkWrapper>
            <p className="as-body2">
              <Translate msg="onboarding.fastPass.infoPoint.title_1" />
            </p>
          </InfoPoint>

          <InfoPoint>
            <CheckmarkWrapper>
              <IconWhiteCheckmark />
            </CheckmarkWrapper>
            <p className="as-body2">
              <Translate msg="onboarding.fastPass.infoPoint.title_2" />
            </p>
          </InfoPoint>

          <InfoPoint>
            <CheckmarkWrapper>
              <IconWhiteCheckmark />
            </CheckmarkWrapper>
            <p className="as-body2">
              <Translate msg="onboarding.fastPass.infoPoint.title_3" />
            </p>
          </InfoPoint>
        </InfoWrapper>

        <ButtonsWrap>
          <ButtonWrap>
            <Button
              onPress={handleScheduleVisit}
              style={{ width: "100%" }}
              theme="charcoal"
            >
              <Translate msg="onboarding.fastPass.scheduleVisit" />
            </Button>
          </ButtonWrap>
          <ButtonWrap>
            <Button
              onPress={handleSkipVisit}
              theme="transparent"
              outline
              hideArrow
              style={{ width: "100%" }}
            >
              <Translate msg="onboarding.fastPass.skipVisit" />
            </Button>
          </ButtonWrap>
        </ButtonsWrap>
      </Container>
    </StyledInDialog>
  );
};

export default FastPassPopup;
